import { Button, Divider, Grid, Paper, Typography } from "@mui/material";
import { revalidateLogic } from "@tanstack/react-form";
import { useTranslation } from "react-i18next";

import type { DeliveryObject } from "@/libs/generated/types";
import ComponentContainerAccordion from "../../shared/components/ComponentContainerAccordion";
import DocumentsComponentV3 from "../components/DocumentsComponentV3";
import { lastStepSchema } from "../form-config";
import { PriceOptionsSection } from "../sections/PriceOptionsSection";
import Summary from "../summary";
import type { DeliveryFormType, SummaryData } from "../types";
import { scrollToFirstError } from "../utils/scrollToFirstError";

interface Props {
  form: DeliveryFormType;
  data?: DeliveryObject;
  type: string;
  isAdmin: boolean;
  onStepValid: () => void;
  onPrevious: () => void;
}

export function StepSummary({ form, data, type, isAdmin, onStepValid, onPrevious }: Props) {
  const { t } = useTranslation();
  const values = form.state.values;
  const isRestitution = type === "two-way-delivery";

  const v1 = values.step1.vehicle;
  const v2 = values.step2.returnVehicle;

  const summaryData: SummaryData = {
    id: data?.id,
    type,
    clientFirstName: values.step0.clientFirstName,
    clientLastName: values.step0.clientLastName,
    clientPhone: values.step0.clientPhone,
    clientEmail: values.step0.clientEmail,
    clientCompany: values.step0.clientCompany,
    clientSiret: values.step0.clientSiret,
    clientDrivingLicenceNumber: values.step0.clientDrivingLicenceNumber,
    representativeFirstName: values.step0.representativeFirstName,
    representativeLastName: values.step0.representativeLastName,
    representativePhone: values.step0.representativePhone,
    representativeEmail: values.step0.representativeEmail,
    representativeCompany: values.step0.representativeCompany,
    representativeSiret: values.step0.representativeSiret || "",
    carAvailabilityDate: values.step0.carAvailabilityDate,
    carPickUpDate: values.step0.carPickUpDate,
    carDeliveryDeadline: values.step0.carDeliveryDeadline,
    fromAddress: values.step1.fromAddress,
    toAddress: values.step1.toAddress,
    toReturnFromAddress: values.step2.toReturnFromAddress,
    toReturnToAddress: values.step2.toReturnToAddress,
    vehicles: [
      {
        vin: v1.vin,
        vehicleCode: v1.vehicleCode,
        marque: v1.marque,
        model: v1.model,
        type: v1.type,
        carType: v1.carType || "A",
      },
      ...(isRestitution
        ? [
            {
              vin: v2.vin,
              vehicleCode: v2.vehicleCode,
              marque: v2.marque,
              model: v2.model,
              type: v2.type,
              carType: v2.carType || "A",
            },
          ]
        : []),
    ],
    category: values.lastStep.category || "STANDARD",
    comment: values.lastStep.comment,
    selectedOptions: values.lastStep.selectedOptions.selected.map((opt) => ({
      id: Number(opt.id),
      optionId: Number(opt.id),
      name: opt.name,
      clientPrice: opt.price,
    })),
    organization: { slug: "" },
  };

  return (
    <form.FormGroup
      name="lastStep"
      validators={{ onDynamic: lastStepSchema }}
      validationLogic={revalidateLogic()}
      onGroupSubmit={() => onStepValid()}
      onGroupSubmitInvalid={scrollToFirstError}
    >
      {(formGroup) => (
        <form
          onSubmit={(e) => {
            e.preventDefault();
            e.stopPropagation();
            formGroup.handleSubmit();
          }}
        >
          <Grid container spacing={4}>
            <DocumentsComponentV3
              form={form}
              folder={`delivery/${data?.id}/docs/`}
              deliveryId={data?.id}
            />

            <Grid size={{ xs: 12 }}>
              <PriceOptionsSection form={form} type={type} isAdmin={isAdmin} />
            </Grid>

            <Grid size={{ xs: 12 }}>
              <Paper elevation={4} sx={paperSx}>
                <Grid container direction="row" spacing={2} paddingBottom={2}>
                  <Grid size={{ xs: 12 }}>
                    <Typography variant="h5" textTransform="capitalize">
                      {t("delivery.form.comment")}
                    </Typography>
                  </Grid>
                  <Grid size={{ xs: 12 }}>
                    <Divider sx={dividerSx} />
                  </Grid>
                  <Grid size={{ xs: 12 }}>
                    <form.AppField name="lastStep.comment">
                      {(field) => (
                        <field.TextField label={t("delivery.form.comment")} multiline rows={4} />
                      )}
                    </form.AppField>
                  </Grid>
                </Grid>
              </Paper>
            </Grid>

            <Grid size={{ xs: 12 }}>
              <ComponentContainerAccordion title={t("delivery.form.summary")}>
                <Summary
                  data={summaryData}
                  isRestitution={isRestitution}
                  isCreation={!data?.id}
                  editable={false}
                />
              </ComponentContainerAccordion>
            </Grid>

            {/* Navigation buttons */}
            <Grid size={{ xs: 12 }}>
              <Grid container spacing={1}>
                <Grid size={{ md: 6, xs: 12 }}>
                  <Button color="secondary" fullWidth onClick={onPrevious} variant="contained">
                    {t("common.navigation.previous")}
                  </Button>
                </Grid>
                <Grid size={{ md: 6, xs: 12 }}>
                  <Button color="primary" fullWidth type="submit" variant="contained">
                    {t("common.actions.save")}
                  </Button>
                </Grid>
              </Grid>
            </Grid>
          </Grid>
        </form>
      )}
    </form.FormGroup>
  );
}

const paperSx = { backgroundColor: "#E7E6F7", borderRadius: 1, padding: "20px", height: "100%" };
const dividerSx = { bgcolor: "#fe5d8d", marginBottom: 5 };

export default StepSummary;
